import { LinkOutlined, PrinterOutlined } from '@ant-design/icons';
import { Card, Divider, List, Rate, Skeleton, Space } from 'antd';
import * as React from 'react';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { getById } from '../../knowledge-base-common/actions';
import { NO_DATA } from '../../knowledge-base-common/knowledge-base-common.constants';
import { round } from '../../knowledge-base-common/knowledge-base-common.utils';
import { getPrintForm, updateRating } from '../actions';
import { FullArticle, NoContentArticle, PrinFormDTO } from '../knowledge-base.type';
import { getArticleLink } from '../knowledge-base.utils';
import ArticleStatistics from './article-statistics';
import { localize } from '/react4xp/mixins/helpers';
import Button from '/react4xp/mixins/ui/button/button';
import { ComponentContext } from '/react4xp/parts/knowledge-base/knowledge-base';

interface ArticleViewProps {
    article: NoContentArticle;
    section: string;
    category: string;
    tag: string;
}

const ArticleView = memo(({ article, section, category, tag }: ArticleViewProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [fullArticle, setFullArticle] = useState<FullArticle>(null);
    const [myVote, setMyVote] = useState<number>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const { clientUrl, serviceUrl, translations, commonTranslations } =
        useContext(ComponentContext);

    useEffect(() => {
        setIsLoading(true);
        getById<FullArticle>(serviceUrl, article.id)
            .then(setFullArticle)
            .finally(() => setIsLoading(false));
    }, [article]);

    const handleVote = useCallback(async (value: number) => {
        if (value === 0 || myVote !== null) return;
        setIsLoading(true);
        try {
            const result = await updateRating(serviceUrl, article.id, value);
            setFullArticle((prev) => ({ ...prev, ...result }));
            setMyVote(value);
        } finally {
            setIsLoading(false);
        }
    }, []);

    if (!fullArticle || isLoading) return <Skeleton />;

    const scoreDescription = `${localize('rating', translations)}: ${
        round(fullArticle.rating || 0) ?? NO_DATA
    }/5 (${fullArticle.scores || localize('no-scores', translations)})`;

    const getPrintFormPdf = async () => {
        setIsDownloading(true);

        const pdfDto: PrinFormDTO = {
            title: fullArticle.title,
            content: fullArticle.content,
            updatedAt: fullArticle.updatedAt,
            section,
            category,
            tag,
            url: getArticleLink(fullArticle.id, clientUrl),
        };

        // const isVerified = await executeRecaptcha('knowledge_base_print_pdf');
        // if (!isVerified) {
        //     return;
        // }

        const pdf = (await getPrintForm(pdfDto)).data;

        const filename = `${fullArticle.title}.pdf`;
        const url = window.URL.createObjectURL(new Blob([pdf]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        setIsDownloading(false);
    };

    return (
        <Card style={{ position: 'relative' }}>
            <ArticleStatistics
                published={fullArticle.updatedAt}
                views={fullArticle.views}
            />
            <p
                className="title"
                style={{ margin: '1rem 0' }}
            >
                {fullArticle.title}
            </p>
            <div
                className="article-view-content"
                dangerouslySetInnerHTML={{ __html: fullArticle.content }}
                style={{ wordBreak: 'break-word', wordWrap: 'break-word' }}
            />
            {fullArticle.linked.length > 0 && (
                <>
                    <Divider />
                    <List
                        size="small"
                        header={<div>{localize('linked-articles', translations)}</div>}
                        bordered
                        dataSource={fullArticle.linked}
                        renderItem={(item) => (
                            <List.Item>
                                <a href={getArticleLink(item.value, clientUrl)}>
                                    <Space>
                                        <LinkOutlined />
                                        {item.label}
                                    </Space>
                                </a>
                            </List.Item>
                        )}
                    />
                </>
            )}
            <Divider />
            <Space direction="vertical">
                {myVote === null && <p className="muted">{localize('cta.rate', translations)}</p>}
                <Space
                    align="end"
                    size={12}
                >
                    <Rate
                        allowHalf
                        defaultValue={myVote ?? 0}
                        onChange={handleVote}
                        disabled={isLoading || myVote !== null}
                    />
                    <span className="rating-label">{myVote ?? ''}</span>
                </Space>
                <p className="muted">{scoreDescription}</p>
            </Space>

            <Button
                icon={<PrinterOutlined />}
                style={{ position: 'absolute', top: 16, right: 16 }}
                onClick={getPrintFormPdf}
                loading={isDownloading}
            >
                {localize('button.print', commonTranslations)}
            </Button>
        </Card>
    );
});

export default ArticleView;
